import L from 'leaflet'
import { IMapService, ParkingSpot, UserLocation, MapBounds, GeolocationError } from './IMapService'
import { overpassService, ParkingDetail } from './overpassService'

// Fix pour les icônes Leaflet par défaut
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

export class OpenStreetMapService implements IMapService {
  private map: L.Map | null = null
  private markers: L.Marker[] = []

  private isMapUsable(map: L.Map | null | undefined): map is L.Map {
    if (!map) return false

    try {
      const container = typeof map.getContainer === 'function' ? map.getContainer() : null
      const markerPane = typeof map.getPane === 'function' ? map.getPane('markerPane') : null
      return !!container && container.isConnected && !!markerPane
    } catch (error) {
      return false
    }
  }

  async loadMap(): Promise<void> {
    // Leaflet est déjà chargé via npm, pas besoin de charger dynamiquement
    return Promise.resolve()
  }

  async initializeMap(element: HTMLElement, center: UserLocation): Promise<L.Map> {
    if (this.map) {
      try {
        this.cleanup()
      } catch (error) {
        console.warn('Failed to cleanup previous Leaflet map:', error)
      }
    }

    // Vérifier si l'élément a déjà une carte Leaflet
    if ((element as any)._leaflet_id) {
      // Supprimer l'ancienne carte
      const existingMap = (element as any)._leaflet_map
      if (existingMap) {
        existingMap.remove()
      }
      // Nettoyer l'élément
      element.innerHTML = ''
      delete (element as any)._leaflet_id
      delete (element as any)._leaflet_map
    }

    // Créer la carte Leaflet
    const map = L.map(element, {
      center: [center.latitude, center.longitude],
      zoom: 17,
      zoomControl: false,
      attributionControl: true,
      worldCopyJump: true,
    })

    // Stocker la référence de la carte sur l'élément pour le nettoyage futur
    ;(element as any)._leaflet_map = map

    // Ajouter les tuiles OpenStreetMap
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
      maxZoom: 19,
    }).addTo(map)

    this.map = map
    return map
  }

  async getCurrentLocation(): Promise<UserLocation & { method?: string }> {
    if (!window.isSecureContext) {
      throw new GeolocationError('INSECURE_CONTEXT', 'La géolocalisation nécessite HTTPS sur iPhone/Safari.')
    }

    // Essayer la géolocalisation haute précision d'abord
    try {
      const location = await this.getHighAccuracyLocation()
      console.log('High accuracy location obtained')
      return { ...location, method: 'GPS (High Accuracy)' }
    } catch (error) {
      if (error instanceof GeolocationError && error.code === 'PERMISSION_DENIED') {
        throw error
      }
      console.warn('High accuracy location failed:', error)
    }

    // Essayer la géolocalisation réseau
    try {
      const location = await this.getNetworkLocation()
      console.log('Network-based location obtained')
      return { ...location, method: 'Network/WiFi' }
    } catch (error) {
      if (error instanceof GeolocationError && error.code === 'PERMISSION_DENIED') {
        throw error
      }
      console.warn('Network-based location failed:', error)
    }

    // Essayer la géolocalisation IP
    try {
      const location = await this.getIPLocation()
      console.log('IP-based location obtained')
      return { ...location, method: 'IP Geolocation' }
    } catch (error) {
      console.warn('IP-based location failed:', error)
    }

    throw new GeolocationError('POSITION_UNAVAILABLE', 'Impossible de déterminer votre position.')
  }

  private async getHighAccuracyLocation(): Promise<UserLocation> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new GeolocationError('UNSUPPORTED', 'Geolocation not supported'))
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            reject(new GeolocationError('PERMISSION_DENIED', 'Permission de localisation refusée'))
            return
          }
          if (error.code === error.TIMEOUT) {
            reject(new GeolocationError('TIMEOUT', 'Délai de localisation dépassé'))
            return
          }
          if (error.code === error.POSITION_UNAVAILABLE) {
            reject(new GeolocationError('POSITION_UNAVAILABLE', 'Position indisponible'))
            return
          }
          reject(new GeolocationError('UNKNOWN', `High accuracy geolocation failed: ${error.message}`))
        },
        {
          enableHighAccuracy: true,
          timeout: 15000,
          maximumAge: 300000,
        }
      )
    })
  }

  private async getNetworkLocation(): Promise<UserLocation> {
    return new Promise((resolve, reject) => {
      if (!navigator.geolocation) {
        reject(new GeolocationError('UNSUPPORTED', 'Geolocation not supported'))
        return
      }

      navigator.geolocation.getCurrentPosition(
        (position) => {
          resolve({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
        },
        (error) => {
          if (error.code === error.PERMISSION_DENIED) {
            reject(new GeolocationError('PERMISSION_DENIED', 'Permission de localisation refusée'))
            return
          }
          if (error.code === error.TIMEOUT) {
            reject(new GeolocationError('TIMEOUT', 'Délai de localisation dépassé'))
            return
          }
          if (error.code === error.POSITION_UNAVAILABLE) {
            reject(new GeolocationError('POSITION_UNAVAILABLE', 'Position indisponible'))
            return
          }
          reject(new GeolocationError('UNKNOWN', `Network geolocation failed: ${error.message}`))
        },
        {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 600000,
        }
      )
    })
  }

  private async getIPLocation(): Promise<UserLocation> {
    try {
      const services = [
        'https://ipapi.co/json/',
        'https://ip-api.com/json/',
        'https://ipinfo.io/json'
      ]

      for (const service of services) {
        try {
          const response = await fetch(service)
          if (!response.ok) continue
          
          const data = await response.json()
          
          let lat: number, lon: number
          
          if (service.includes('ipapi.co')) {
            lat = data.latitude
            lon = data.longitude
          } else if (service.includes('ip-api.com')) {
            lat = data.lat
            lon = data.lon
          } else if (service.includes('ipinfo.io')) {
            const [latStr, lonStr] = data.loc.split(',')
            lat = parseFloat(latStr)
            lon = parseFloat(lonStr)
          } else {
            continue
          }

          if (lat && lon && !isNaN(lat) && !isNaN(lon)) {
            return {
              latitude: lat,
              longitude: lon
            }
          }
        } catch (error) {
          console.warn(`IP location service ${service} failed:`, error)
          continue
        }
      }
      
      throw new Error('All IP location services failed')
    } catch (error) {
      throw new Error(`IP geolocation failed: ${error}`)
    }
  }

  async requestLocationPermission(): Promise<boolean> {
    try {
      if ('permissions' in navigator) {
        const permission = await navigator.permissions.query({ name: 'geolocation' })
        return permission.state === 'granted'
      }
      
      await this.getHighAccuracyLocation()
      return true
    } catch (error) {
      return false
    }
  }

  getMapBounds(map: L.Map): MapBounds {
    const bounds = map.getBounds()
    const center = bounds.getCenter()

    // Calculer le rayon visible de la map (distance centre → coin)
    const ne = bounds.getNorthEast()
    const visibleRadius = this.calculateDistance(
      center.lat,
      center.lng,
      ne.lat,
      ne.lng
    )

    // Adapter le rayon au zoom avec un max de 5km
    const radius = Math.min(visibleRadius, 5000)

    return {
      center: { latitude: center.lat, longitude: center.lng },
      radius
    }
  }

  setMapCenter(map: L.Map, location: UserLocation): void {
    map.setView([location.latitude, location.longitude])
  }

  setMapZoom(map: L.Map, zoom: number): void {
    map.setZoom(zoom)
  }

  addMarker(map: L.Map, position: UserLocation, title: string, isUser: boolean = false, variant?: 'default' | 'parked' | 'selected'): L.Marker {
    let icon: L.Icon | L.DivIcon

    if (variant === 'selected') {
      icon = L.divIcon({
        html: `
          <div style="position: relative; width: 72px; height: 72px; display:flex; align-items:center; justify-content:center;">
            <div style="position:absolute; width:36px; height:36px; border-radius:9999px; background:#10B98155; animation:selectedPulse 1.8s infinite;"></div>
            <div style="position:absolute; width:24px; height:24px; border-radius:9999px; background:#10B98166; animation:selectedPulse 1.8s .4s infinite;"></div>
            <div style="position:relative; width:32px; height:32px; background:#10B981; border:3px solid white; border-radius:9999px; box-shadow:0 10px 25px rgba(16,185,129,.35); display:flex; align-items:center; justify-content:center; z-index:2;">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18.5 13H17V10C17 8.9 16.1 8 15 8H9C7.9 8 7 8.9 7 10V13H5.5C5.22 13 5 13.22 5 13.5V16.5C5 16.78 5.22 17 5.5 17H6V18C6 18.55 6.45 19 7 19C7.55 19 8 18.55 8 18V17H16V18C16 18.55 16.45 19 17 19C17.55 19 18 18.55 18 18V17H18.5C18.78 17 19 16.78 19 16.5V13.5C19 13.22 18.78 13 18.5 13ZM8.5 10.5C8.5 10.22 8.72 10 9 10H15C15.28 10 15.5 10.22 15.5 10.5V12.5C15.5 12.78 15.28 13 15 13H9C8.72 13 8.5 12.78 8.5 12.5V10.5Z" fill="white"/>
              </svg>
            </div>
          </div>
          <style>
            @keyframes selectedPulse {
              0% { transform: scale(0.75); opacity: .7; }
              100% { transform: scale(1.9); opacity: 0; }
            }
          </style>
        `,
        className: 'selected-car-marker',
        iconSize: [72, 72],
        iconAnchor: [36, 36]
      })
    } else if (variant === 'parked') {
      icon = L.divIcon({
        html: `
          <div style="
            width: 40px;
            height: 40px;
            background-color: #3B82F6;
            border: 3px solid white;
            border-radius: 50%;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18.5 13H17V10C17 8.9 16.1 8 15 8H9C7.9 8 7 8.9 7 10V13H5.5C5.22 13 5 13.22 5 13.5V16.5C5 16.78 5.22 17 5.5 17H6V18C6 18.55 6.45 19 7 19C7.55 19 8 18.55 8 18V17H16V18C16 18.55 16.45 19 17 19C17.55 19 18 18.55 18 18V17H18.5C18.78 17 19 16.78 19 16.5V13.5C19 13.22 18.78 13 18.5 13ZM8.5 10.5C8.5 10.22 8.72 10 9 10H15C15.28 10 15.5 10.22 15.5 10.5V12.5C15.5 12.78 15.28 13 15 13H9C8.72 13 8.5 12.78 8.5 12.5V10.5Z" fill="white"/>
            </svg>
          </div>
        `,
        className: 'parked-vehicle-marker',
        iconSize: [40, 40],
        iconAnchor: [20, 20]
      })
    } else if (isUser) {
      // Icône personnalisée pour l'utilisateur avec animation
      icon = L.divIcon({
        html: `
          <div style="position: relative; width: 40px; height: 40px;">
            <div style="
              position: absolute;
              width: 40px;
              height: 40px;
              background-color: #00B04F;
              border-radius: 50%;
              opacity: 0.6;
              animation: pulse 1.5s infinite;
            "></div>
            <div style="
              position: absolute;
              top: 10px;
              left: 10px;
              width: 20px;
              height: 20px;
              background-color: #00B04F;
              border: 3px solid white;
              border-radius: 50%;
            "></div>
          </div>
          <style>
            @keyframes pulse {
              0% { transform: scale(0.5); opacity: 0.6; }
              100% { transform: scale(1.5); opacity: 0; }
            }
          </style>
        `,
        className: 'user-location-marker',
        iconSize: [40, 40],
        iconAnchor: [20, 20]
      })
    } else {
      // Icône personnalisée pour les parkings
      icon = L.divIcon({
        html: `
          <div style="
            width: 30px;
            height: 30px;
            background-color: #00B04F;
            border: 2px solid white;
            border-radius: 50% 50% 50% 0;
            transform: rotate(-45deg);
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
            display: flex;
            align-items: center;
            justify-content: center;
          ">
            <div style="
              color: white;
              font-size: 12px;
              font-weight: bold;
              transform: rotate(45deg);
            ">P</div>
          </div>
        `,
        className: 'parking-marker',
        iconSize: [30, 30],
        iconAnchor: [15, 30]
      })
    }

    const marker = L.marker([position.latitude, position.longitude], { icon }).bindTooltip(title)

    const tryAttachMarker = () => {
      if (!this.isMapUsable(map) || (marker as any)._map) {
        return
      }

      try {
        marker.addTo(map)
      } catch (error) {
        console.warn('Leaflet marker could not be attached to map:', error)
      }
    }

    if (this.isMapUsable(map)) {
      tryAttachMarker()
    } else if (typeof (map as any)?.whenReady === 'function') {
      ;(map as any).whenReady(() => {
        tryAttachMarker()
      })
    }

    this.markers.push(marker)
    return marker
  }

  removeMarker(marker: L.Marker): void {
    marker.remove()
    const index = this.markers.indexOf(marker)
    if (index > -1) {
      this.markers.splice(index, 1)
    }
  }

  clearMarkers(markers: L.Marker[]): void {
    markers.forEach(marker => {
      marker.remove()
      const index = this.markers.indexOf(marker)
      if (index > -1) {
        this.markers.splice(index, 1)
      }
    })
  }

  async searchNearbyParkings(location: UserLocation, radius: number = 2000): Promise<ParkingSpot[]> {
    try {
      return await overpassService.searchParkings(location, radius)
    } catch (error) {
      console.error('Error searching nearby parkings:', error)
      return []
    }
  }

  async fetchParkingDetail(placeId: string): Promise<ParkingDetail | null> {
    return overpassService.fetchParkingDetail(placeId)
  }

  async searchParkingsInBounds(map: L.Map, userLocation?: UserLocation): Promise<ParkingSpot[]> {
    const { center, radius } = this.getMapBounds(map)
    
    const parkingSpots = await this.searchNearbyParkings(center, radius)
    
    // Calculer les distances depuis la position utilisateur si disponible
    const referenceLocation = userLocation || center
    parkingSpots.forEach(spot => {
      spot.distance = this.calculateDistance(
        referenceLocation.latitude,
        referenceLocation.longitude,
        spot.latitude,
        spot.longitude
      )
    })
    
    return parkingSpots.sort((a, b) => (a.distance || 0) - (b.distance || 0))
  }

  calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371 // Rayon de la Terre en kilomètres
    const dLat = this.deg2rad(lat2 - lat1)
    const dLon = this.deg2rad(lon2 - lon1)
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    const d = R * c // Distance en kilomètres
    return Math.round(d * 1000) // Convertir en mètres
  }

  private deg2rad(deg: number): number {
    return deg * (Math.PI / 180)
  }

  openDirections(destination: ParkingSpot): void {
    // Détecter le type d'appareil et utiliser l'app de navigation appropriée
    const lat = destination.latitude
    const lon = destination.longitude
    // Détecter iOS
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent)
    // Détecter Android
    const isAndroid = /Android/.test(navigator.userAgent)
    
    if (isIOS) {
      // Sur iOS, essayer d'ouvrir Apple Maps en premier
      const appleMapsUrl = `maps://maps.apple.com/?daddr=${lat},${lon}&dirflg=d`
      window.location.href = appleMapsUrl
      
      // Fallback vers Google Maps si Apple Maps n'est pas disponible
      setTimeout(() => {
        const googleMapsUrl = `https://maps.google.com/maps?daddr=${lat},${lon}&dirflg=d`
        window.open(googleMapsUrl, '_blank')
      }, 1000)
    } else if (isAndroid) {
      // Sur Android, utiliser l'intent Google Maps
      const googleMapsIntent = `google.navigation:q=${lat},${lon}&mode=d`
      window.location.href = googleMapsIntent
      
      // Fallback vers l'URL web Google Maps
      setTimeout(() => {
        const googleMapsUrl = `https://maps.google.com/maps?daddr=${lat},${lon}&dirflg=d`
        window.open(googleMapsUrl, '_blank')
      }, 1000)
    } else {
      // Sur desktop ou autres plateformes, utiliser Google Maps web
      const googleMapsUrl = `https://maps.google.com/maps?daddr=${lat},${lon}&dirflg=d`
      window.open(googleMapsUrl, '_blank')
    }
  }

  cleanup(): void {
    if (this.map) {
      this.clearMarkers(this.markers)
      this.map.remove()
      this.map = null
    }
    this.markers = []
  }
}
